"use strict";

exports.__esModule = true;
exports.Bone = void 0;

var _BoneData = require("./BoneData");

var _Utils = require("./Utils");

/******************************************************************************
 * Spine Runtimes License Agreement
 * Last updated January 1, 2020. Replaces all prior versions.
 *
 * Copyright (c) 2013-2020, Esoteric Software LLC
 *
 * Integration of the Spine Runtimes into software or otherwise creating
 * derivative works of the Spine Runtimes is permitted under the terms and
 * conditions of Section 2 of the Spine Editor License Agreement:
 * http://esotericsoftware.com/spine-editor-license
 *
 * Otherwise, it is permitted to integrate the Spine Runtimes into software
 * or otherwise create derivative works of the Spine Runtimes (collectively,
 * "Products"), provided that each user of the Products must obtain their own
 * Spine Editor license and redistribution of the Products in any form must
 * include this license and copyright notice.
 *
 * THE SPINE RUNTIMES ARE PROVIDED BY ESOTERIC SOFTWARE LLC "AS IS" AND ANY
 * EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL ESOTERIC SOFTWARE LLC BE LIABLE FOR ANY
 * DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES,
 * BUSINESS INTERRUPTION, OR LOSS OF USE, DATA, OR PROFITS) HOWEVER CAUSED AND
 * ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF
 * THE SPINE RUNTIMES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 *****************************************************************************/

/** Stores a bone's current pose.
 *
 * A bone has a local transform which is used to compute its world transform. A bone also has an applied transform, which is a
 * local transform that can be applied to compute the world transform. The local transform and applied transform may differ if a
 * constraint or application code modifies the world transform after it was computed from the local transform. */
var Bone = /*#__PURE__*/function () {
  /** @param parent May be null. */
  function Bone(data, skeleton, parent) {
    /** The bone's setup pose data. */
    this.data = null;
    /** The skeleton this bone belongs to. */

    this.skeleton = null;
    /** The parent bone, or null if this is the root bone. */

    this.parent = null;
    /** The immediate children of this bone. */

    this.children = new Array();
    /** The local x translation. */

    this.x = 0;
    /** The local y translation. */

    this.y = 0;
    /** The local rotation in degrees, counter clockwise. */

    this.rotation = 0;
    /** The local scaleX. */

    this.scaleX = 0;
    /** The local scaleY. */

    this.scaleY = 0;
    /** The local shearX. */

    this.shearX = 0;
    /** The local shearY. */

    this.shearY = 0;
    /** The applied local x translation. */

    this.ax = 0;
    /** The applied local y translation. */

    this.ay = 0;
    /** The applied local rotation in degrees, counter clockwise. */

    this.arotation = 0;
    /** The applied local scaleX. */

    this.ascaleX = 0;
    /** The applied local scaleY. */

    this.ascaleY = 0;
    /** The applied local shearX. */

    this.ashearX = 0;
    /** The applied local shearY. */

    this.ashearY = 0;
    /** Part of the world transform matrix for the X axis. If changed, {@link #updateAppliedTransform()} should be called. */

    this.a = 0;
    /** Part of the world transform matrix for the Y axis. If changed, {@link #updateAppliedTransform()} should be called. */

    this.b = 0;
    /** Part of the world transform matrix for the X axis. If changed, {@link #updateAppliedTransform()} should be called. */

    this.c = 0;
    /** Part of the world transform matrix for the Y axis. If changed, {@link #updateAppliedTransform()} should be called. */

    this.d = 0;
    /** The world X position. If changed, {@link #updateAppliedTransform()} should be called. */

    this.worldY = 0;
    /** The world Y position. If changed, {@link #updateAppliedTransform()} should be called. */

    this.worldX = 0;
    this.sorted = false;
    this.active = false;
    if (!data) throw new Error("data cannot be null.");
    if (!skeleton) throw new Error("skeleton cannot be null.");
    this.data = data;
    this.skeleton = skeleton;
    this.parent = parent;
    this.setToSetupPose();
  }
  /** Returns false when the bone has not been computed because {@link BoneData#skinRequired} is true and the
    * {@link Skeleton#skin active skin} does not {@link Skin#bones contain} this bone. */


  var _proto = Bone.prototype;

  _proto.isActive = function isActive() {
    return this.active;
  }
  /** Computes the world transform using the parent bone and this bone's local applied transform. */
  ;

  _proto.update = function update() {
    this.updateWorldTransformWith(this.ax, this.ay, this.arotation, this.ascaleX, this.ascaleY, this.ashearX, this.ashearY);
  }
  /** Computes the world transform using the parent bone and this bone's local transform.
   *
   * See {@link #updateWorldTransformWith()}. */
  ;

  _proto.updateWorldTransform = function updateWorldTransform() {
    this.updateWorldTransformWith(this.x, this.y, this.rotation, this.scaleX, this.scaleY, this.shearX, this.shearY);
  }
  /** Computes the world transform using the parent bone and the specified local transform. The applied transform is set to the
   * specified local transform. Child bones are not updated.
   *
   * See [World transforms](http://esotericsoftware.com/spine-runtime-skeletons#World-transforms) in the Spine
   * Runtimes Guide. */
  ;

  _proto.updateWorldTransformWith = function updateWorldTransformWith(x, y, rotation, scaleX, scaleY, shearX, shearY) {
    this.ax = x;
    this.ay = y;
    this.arotation = rotation;
    this.ascaleX = scaleX;
    this.ascaleY = scaleY;
    this.ashearX = shearX;
    this.ashearY = shearY;
    var parent = this.parent;

    if (!parent) {
      // Root bone.
      var skeleton = this.skeleton;
      var rotationY = rotation + 90 + shearY;
      var sx = skeleton.scaleX;
      var sy = skeleton.scaleY;
      this.a = _Utils.MathUtils.cosDeg(rotation + shearX) * scaleX * sx;
      this.b = _Utils.MathUtils.cosDeg(rotationY) * scaleY * sx;
      this.c = _Utils.MathUtils.sinDeg(rotation + shearX) * scaleX * sy;
      this.d = _Utils.MathUtils.sinDeg(rotationY) * scaleY * sy;
      this.worldX = x * sx + skeleton.x;
      this.worldY = y * sy + skeleton.y;
      return;
    }

    var pa = parent.a,
        pb = parent.b,
        pc = parent.c,
        pd = parent.d;
    this.worldX = pa * x + pb * y + parent.worldX;
    this.worldY = pc * x + pd * y + parent.worldY;

    switch (this.data.transformMode) {
      case _BoneData.TransformMode.Normal:
        {
          var _rotationY = rotation + 90 + shearY;

          var la = _Utils.MathUtils.cosDeg(rotation + shearX) * scaleX;
          var lb = _Utils.MathUtils.cosDeg(_rotationY) * scaleY;
          var lc = _Utils.MathUtils.sinDeg(rotation + shearX) * scaleX;
          var ld = _Utils.MathUtils.sinDeg(_rotationY) * scaleY;
          this.a = pa * la + pb * lc;
          this.b = pa * lb + pb * ld;
          this.c = pc * la + pd * lc;
          this.d = pc * lb + pd * ld;
          return;
        }

      case _BoneData.TransformMode.OnlyTranslation:
        {
          var _rotationY2 = rotation + 90 + shearY;

          this.a = _Utils.MathUtils.cosDeg(rotation + shearX) * scaleX;
          this.b = _Utils.MathUtils.cosDeg(_rotationY2) * scaleY;
          this.c = _Utils.MathUtils.sinDeg(rotation + shearX) * scaleX;
          this.d = _Utils.MathUtils.sinDeg(_rotationY2) * scaleY;
          break;
        }

      case _BoneData.TransformMode.NoRotationOrReflection:
        {
          var s = pa * pa + pc * pc;
          var prx = 0;

          if (s > 0.0001) {
            s = Math.abs(pa * pd - pb * pc) / s;
            pa /= this.skeleton.scaleX;
            pc /= this.skeleton.scaleY;
            pb = pc * s;
            pd = pa * s;
            prx = Math.atan2(pc, pa) * _Utils.MathUtils.radDeg;
          } else {
            pa = 0;
            pc = 0;
            prx = 90 - Math.atan2(pd, pb) * _Utils.MathUtils.radDeg;
          }

          var rx = rotation + shearX - prx;
          var ry = rotation + shearY - prx + 90;

          var _la = _Utils.MathUtils.cosDeg(rx) * scaleX;

          var _lb = _Utils.MathUtils.cosDeg(ry) * scaleY;

          var _lc = _Utils.MathUtils.sinDeg(rx) * scaleX;

          var _ld = _Utils.MathUtils.sinDeg(ry) * scaleY;

          this.a = pa * _la - pb * _lc;
          this.b = pa * _lb - pb * _ld;
          this.c = pc * _la + pd * _lc;
          this.d = pc * _lb + pd * _ld;
          break;
        }

      case _BoneData.TransformMode.NoScale:
      case _BoneData.TransformMode.NoScaleOrReflection:
        {
          var cos = _Utils.MathUtils.cosDeg(rotation);

          var sin = _Utils.MathUtils.sinDeg(rotation);

          var za = (pa * cos + pb * sin) / this.skeleton.scaleX;
          var zc = (pc * cos + pd * sin) / this.skeleton.scaleY;

          var _s = Math.sqrt(za * za + zc * zc);

          if (_s > 0.00001) _s = 1 / _s;
          za *= _s;
          zc *= _s;
          _s = Math.sqrt(za * za + zc * zc);
          if (this.data.transformMode == _BoneData.TransformMode.NoScale && pa * pd - pb * pc < 0 != (this.skeleton.scaleX < 0 != this.skeleton.scaleY < 0)) _s = -_s;
          var r = Math.PI / 2 + Math.atan2(zc, za);

          var zb = Math.cos(r) * _s;

          var zd = Math.sin(r) * _s;

          var _la2 = _Utils.MathUtils.cosDeg(shearX) * scaleX;

          var _lb2 = _Utils.MathUtils.cosDeg(90 + shearY) * scaleY;

          var _lc2 = _Utils.MathUtils.sinDeg(shearX) * scaleX;

          var _ld2 = _Utils.MathUtils.sinDeg(90 + shearY) * scaleY;

          this.a = za * _la2 + zb * _lc2;
          this.b = za * _lb2 + zb * _ld2;
          this.c = zc * _la2 + zd * _lc2;
          this.d = zc * _lb2 + zd * _ld2;
          break;
        }
    }

    this.a *= this.skeleton.scaleX;
    this.b *= this.skeleton.scaleX;
    this.c *= this.skeleton.scaleY;
    this.d *= this.skeleton.scaleY;
  }
  /** Sets this bone's local transform to the setup pose. */
  ;

  _proto.setToSetupPose = function setToSetupPose() {
    var data = this.data;
    this.x = data.x;
    this.y = data.y;
    this.rotation = data.rotation;
    this.scaleX = data.scaleX;
    this.scaleY = data.scaleY;
    this.shearX = data.shearX;
    this.shearY = data.shearY;
  }
  /** The world rotation for the X axis, calculated using {@link #a} and {@link #c}. */
  ;

  _proto.getWorldRotationX = function getWorldRotationX() {
    return Math.atan2(this.c, this.a) * _Utils.MathUtils.radDeg;
  }
  /** The world rotation for the Y axis, calculated using {@link #b} and {@link #d}. */
  ;

  _proto.getWorldRotationY = function getWorldRotationY() {
    return Math.atan2(this.d, this.b) * _Utils.MathUtils.radDeg;
  }
  /** The magnitude (always positive) of the world scale X, calculated using {@link #a} and {@link #c}. */
  ;

  _proto.getWorldScaleX = function getWorldScaleX() {
    return Math.sqrt(this.a * this.a + this.c * this.c);
  }
  /** The magnitude (always positive) of the world scale Y, calculated using {@link #b} and {@link #d}. */
  ;

  _proto.getWorldScaleY = function getWorldScaleY() {
    return Math.sqrt(this.b * this.b + this.d * this.d);
  }
  /** Computes the applied transform values from the world transform.
   *
   * If the world transform is modified (by a constraint, {@link #rotateWorld(float)}, etc) then this method should be called so
   * the applied transform matches the world transform. The applied transform may be needed by other code (eg to apply other
   * constraints).
   *
   * Some information is ambiguous in the world transform, such as -1,-1 scale versus 180 rotation. The applied transform after
   * calling this method is equivalent to the local transform used to compute the world transform, but may not be identical. */
  ;

  _proto.updateAppliedTransform = function updateAppliedTransform() {
    var parent = this.parent;

    if (!parent) {
      this.ax = this.worldX - this.skeleton.x;
      this.ay = this.worldY - this.skeleton.y;
      this.arotation = Math.atan2(this.c, this.a) * _Utils.MathUtils.radDeg;
      this.ascaleX = Math.sqrt(this.a * this.a + this.c * this.c);
      this.ascaleY = Math.sqrt(this.b * this.b + this.d * this.d);
      this.ashearX = 0;
      this.ashearY = Math.atan2(this.a * this.b + this.c * this.d, this.a * this.d - this.b * this.c) * _Utils.MathUtils.radDeg;
      return;
    }

    var pa = parent.a,
        pb = parent.b,
        pc = parent.c,
        pd = parent.d;
    var pid = 1 / (pa * pd - pb * pc);
    var dx = this.worldX - parent.worldX,
        dy = this.worldY - parent.worldY;
    this.ax = dx * pd * pid - dy * pb * pid;
    this.ay = dy * pa * pid - dx * pc * pid;
    var ia = pid * pd;
    var id = pid * pa;
    var ib = pid * pb;
    var ic = pid * pc;
    var ra = ia * this.a - ib * this.c;
    var rb = ia * this.b - ib * this.d;
    var rc = id * this.c - ic * this.a;
    var rd = id * this.d - ic * this.b;
    this.ashearX = 0;
    this.ascaleX = Math.sqrt(ra * ra + rc * rc);

    if (this.ascaleX > 0.0001) {
      var det = ra * rd - rb * rc;
      this.ascaleY = det / this.ascaleX;
      this.ashearY = Math.atan2(ra * rb + rc * rd, det) * _Utils.MathUtils.radDeg;
      this.arotation = Math.atan2(rc, ra) * _Utils.MathUtils.radDeg;
    } else {
      this.ascaleX = 0;
      this.ascaleY = Math.sqrt(rb * rb + rd * rd);
      this.ashearY = 0;
      this.arotation = 90 - Math.atan2(rd, rb) * _Utils.MathUtils.radDeg;
    }
  }
  /** Transforms a point from world coordinates to the bone's local coordinates. */
  ;

  _proto.worldToLocal = function worldToLocal(world) {
    var invDet = 1 / (this.a * this.d - this.b * this.c);
    var x = world.x - this.worldX,
        y = world.y - this.worldY;
    world.x = x * this.d * invDet - y * this.b * invDet;
    world.y = y * this.a * invDet - x * this.c * invDet;
    return world;
  }
  /** Transforms a point from the bone's local coordinates to world coordinates. */
  ;

  _proto.localToWorld = function localToWorld(local) {
    var x = local.x,
        y = local.y;
    local.x = x * this.a + y * this.b + this.worldX;
    local.y = x * this.c + y * this.d + this.worldY;
    return local;
  }
  /** Transforms a world rotation to a local rotation. */
  ;

  _proto.worldToLocalRotation = function worldToLocalRotation(worldRotation) {
    var sin = _Utils.MathUtils.sinDeg(worldRotation),
        cos = _Utils.MathUtils.cosDeg(worldRotation);

    return Math.atan2(this.a * sin - this.c * cos, this.d * cos - this.b * sin) * _Utils.MathUtils.radDeg + this.rotation - this.shearX;
  }
  /** Transforms a local rotation to a world rotation. */
  ;

  _proto.localToWorldRotation = function localToWorldRotation(localRotation) {
    localRotation -= this.rotation - this.shearX;

    var sin = _Utils.MathUtils.sinDeg(localRotation),
        cos = _Utils.MathUtils.cosDeg(localRotation);

    return Math.atan2(cos * this.c + sin * this.d, cos * this.a + sin * this.b) * _Utils.MathUtils.radDeg;
  }
  /** Rotates the world transform the specified amount.
   * <p>
   * After changes are made to the world transform, {@link #updateAppliedTransform()} should be called and {@link #update()} will
   * need to be called on any child bones, recursively. */
  ;

  _proto.rotateWorld = function rotateWorld(degrees) {
    var a = this.a,
        b = this.b,
        c = this.c,
        d = this.d;

    var cos = _Utils.MathUtils.cosDeg(degrees),
        sin = _Utils.MathUtils.sinDeg(degrees);

    this.a = cos * a - sin * c;
    this.b = cos * b - sin * d;
    this.c = sin * a + cos * c;
    this.d = sin * b + cos * d;
  };

  return Bone;
}();

exports.Bone = Bone;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJtYXBwaW5ncyI6Ijs7Ozs7QUE2QkE7O0FBR0E7O0FBaENBOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztBQWtDQTs7Ozs7SUFLYUEsSTtFQTRFWjtFQUNBLGNBQWFDLElBQWIsRUFBNkJDLFFBQTdCLEVBQWlEQyxNQUFqRCxFQUE2RDtJQTVFN0Q7SUFDQSxZQUFpQixJQUFqQjtJQUVBOztJQUNBLGdCQUFxQixJQUFyQjtJQUVBOztJQUNBLGNBQWUsSUFBZjtJQUVBOztJQUNBLGdCQUFXLElBQUlDLEtBQUosRUFBWDtJQUVBOztJQUNBLFNBQUksQ0FBSjtJQUVBOztJQUNBLFNBQUksQ0FBSjtJQUVBOztJQUNBLGdCQUFXLENBQVg7SUFFQTs7SUFDQSxjQUFTLENBQVQ7SUFFQTs7SUFDQSxjQUFTLENBQVQ7SUFFQTs7SUFDQSxjQUFTLENBQVQ7SUFFQTs7SUFDQSxjQUFTLENBQVQ7SUFFQTs7SUFDQSxVQUFLLENBQUw7SUFFQTs7SUFDQSxVQUFLLENBQUw7SUFFQTs7SUFDQSxpQkFBWSxDQUFaO0lBRUE7O0lBQ0EsZUFBVSxDQUFWO0lBRUE7O0lBQ0EsZUFBVSxDQUFWO0lBRUE7O0lBQ0EsZUFBVSxDQUFWO0lBRUE7O0lBQ0EsZUFBVSxDQUFWO0lBRUE7O0lBQ0EsU0FBSSxDQUFKO0lBRUE7O0lBQ0EsU0FBSSxDQUFKO0lBRUE7O0lBQ0EsU0FBSSxDQUFKO0lBRUE7O0lBQ0EsU0FBSSxDQUFKO0lBRUE7O0lBQ0EsY0FBUyxDQUFUO0lBRUE7O0lBQ0EsY0FBUyxDQUFUO0lBRUEsY0FBUyxLQUFUO0lBQ0EsY0FBUyxLQUFUO0lBSUMsSUFBSSxDQUFDSCxJQUFMLEVBQVcsTUFBTSxJQUFJSSxLQUFKLENBQVUsc0JBQVYsQ0FBTjtJQUNYLElBQUksQ0FBQ0gsUUFBTCxFQUFlLE1BQU0sSUFBSUcsS0FBSixDQUFVLDBCQUFWLENBQU47SUFDZixLQUFLSixJQUFMLEdBQVlBLElBQVo7SUFDQSxLQUFLQyxRQUFMLEdBQWdCQSxRQUFoQjtJQUNBLEtBQUtDLE1BQUwsR0FBY0EsTUFBZDtJQUNBLEtBQUtHLGNBQUw7RUFDQTtFQUVEOzs7Ozs7U0FFQUMsUSxHQUFBLG9CQUFRO0lBQ1AsT0FBTyxLQUFLQyxNQUFaO0VBQ0E7RUFFRDs7O1NBQ0FDLE0sR0FBQSxrQkFBTTtJQUNMLEtBQUtDLHdCQUFMLENBQThCLEtBQUtDLEVBQW5DLEVBQXVDLEtBQUtDLEVBQTVDLEVBQWdELEtBQUtDLFNBQXJELEVBQWdFLEtBQUtDLE9BQXJFLEVBQThFLEtBQUtDLE9BQW5GLEVBQTRGLEtBQUtDLE9BQWpHLEVBQTBHLEtBQUtDLE9BQS9HO0VBQ0E7RUFFRDs7Ozs7U0FHQUMsb0IsR0FBQSxnQ0FBb0I7SUFDbkIsS0FBS1Isd0JBQUwsQ0FBOEIsS0FBS1MsQ0FBbkMsRUFBc0MsS0FBS0MsQ0FBM0MsRUFBOEMsS0FBS0MsUUFBbkQsRUFBNkQsS0FBS0MsTUFBbEUsRUFBMEUsS0FBS0MsTUFBL0UsRUFBdUYsS0FBS0MsTUFBNUYsRUFBb0csS0FBS0MsTUFBekc7RUFDQTtFQUVEOzs7Ozs7O1NBS0FmLHdCLEdBQUEsa0NBQTBCUyxDQUExQixFQUFxQ0MsQ0FBckMsRUFBZ0RDLFFBQWhELEVBQWtFQyxNQUFsRSxFQUFrRkMsTUFBbEYsRUFBa0dDLE1BQWxHLEVBQWtIQyxNQUFsSCxFQUFnSTtJQUMvSCxLQUFLZCxFQUFMLEdBQVVRLENBQVY7SUFDQSxLQUFLUCxFQUFMLEdBQVVRLENBQVY7SUFDQSxLQUFLUCxTQUFMLEdBQWlCUSxRQUFqQjtJQUNBLEtBQUtQLE9BQUwsR0FBZVEsTUFBZjtJQUNBLEtBQUtQLE9BQUwsR0FBZVEsTUFBZjtJQUNBLEtBQUtQLE9BQUwsR0FBZVEsTUFBZjtJQUNBLEtBQUtQLE9BQUwsR0FBZVEsTUFBZjtJQUVBLElBQUl0QixNQUFNLEdBQUcsS0FBS0EsTUFBbEI7O0lBQ0EsSUFBSSxDQUFDQSxNQUFMLEVBQWE7TUFBRTtNQUNkLElBQUlELFFBQVEsR0FBRyxLQUFLQSxRQUFwQjtNQUNBLElBQUl3QixTQUFTLEdBQUdMLFFBQVEsR0FBRyxFQUFYLEdBQWdCSSxNQUFoQztNQUNBLElBQUlFLEVBQUUsR0FBR3pCLFFBQVEsQ0FBQ29CLE1BQWxCO01BQ0EsSUFBSU0sRUFBRSxHQUFHMUIsUUFBUSxDQUFDcUIsTUFBbEI7TUFDQSxLQUFLTSxDQUFMLEdBQVNDLGlCQUFVQyxNQUFWLENBQWlCVixRQUFRLEdBQUdHLE1BQTVCLElBQXNDRixNQUF0QyxHQUErQ0ssRUFBeEQ7TUFDQSxLQUFLSyxDQUFMLEdBQVNGLGlCQUFVQyxNQUFWLENBQWlCTCxTQUFqQixJQUE4QkgsTUFBOUIsR0FBdUNJLEVBQWhEO01BQ0EsS0FBS00sQ0FBTCxHQUFTSCxpQkFBVUksTUFBVixDQUFpQmIsUUFBUSxHQUFHRyxNQUE1QixJQUFzQ0YsTUFBdEMsR0FBK0NNLEVBQXhEO01BQ0EsS0FBS08sQ0FBTCxHQUFTTCxpQkFBVUksTUFBVixDQUFpQlIsU0FBakIsSUFBOEJILE1BQTlCLEdBQXVDSyxFQUFoRDtNQUNBLEtBQUtRLE1BQUwsR0FBY2pCLENBQUMsR0FBR1EsRUFBSixHQUFTekIsUUFBUSxDQUFDaUIsQ0FBaEM7TUFDQSxLQUFLa0IsTUFBTCxHQUFjakIsQ0FBQyxHQUFHUSxFQUFKLEdBQVMxQixRQUFRLENBQUNrQixDQUFoQztNQUNBO0lBQ0E7O0lBRUQsSUFBSWtCLEVBQUUsR0FBR25DLE1BQU0sQ0FBQzBCLENBQWhCO0lBQUEsSUFBbUJVLEVBQUUsR0FBR3BDLE1BQU0sQ0FBQzZCLENBQS9CO0lBQUEsSUFBa0NRLEVBQUUsR0FBR3JDLE1BQU0sQ0FBQzhCLENBQTlDO0lBQUEsSUFBaURRLEVBQUUsR0FBR3RDLE1BQU0sQ0FBQ2dDLENBQTdEO0lBQ0EsS0FBS0MsTUFBTCxHQUFjRSxFQUFFLEdBQUduQixDQUFMLEdBQVNvQixFQUFFLEdBQUduQixDQUFkLEdBQWtCakIsTUFBTSxDQUFDaUMsTUFBdkM7SUFDQSxLQUFLQyxNQUFMLEdBQWNHLEVBQUUsR0FBR3JCLENBQUwsR0FBU3NCLEVBQUUsR0FBR3JCLENBQWQsR0FBa0JqQixNQUFNLENBQUNrQyxNQUF2Qzs7SUFFQSxRQUFRLEtBQUtwQyxJQUFMLENBQVV5QyxhQUFsQjtNQUNDLEtBQUtDLHdCQUFjQyxNQUFuQjtRQUEyQjtVQUMxQixJQUFJbEIsVUFBUyxHQUFHTCxRQUFRLEdBQUcsRUFBWCxHQUFnQkksTUFBaEM7O1VBQ0EsSUFBSW9CLEVBQUUsR0FBR2YsaUJBQVVDLE1BQVYsQ0FBaUJWLFFBQVEsR0FBR0csTUFBNUIsSUFBc0NGLE1BQS9DO1VBQ0EsSUFBSXdCLEVBQUUsR0FBR2hCLGlCQUFVQyxNQUFWLENBQWlCTCxVQUFqQixJQUE4QkgsTUFBdkM7VUFDQSxJQUFJd0IsRUFBRSxHQUFHakIsaUJBQVVJLE1BQVYsQ0FBaUJiLFFBQVEsR0FBR0csTUFBNUIsSUFBc0NGLE1BQS9DO1VBQ0EsSUFBSTBCLEVBQUUsR0FBR2xCLGlCQUFVSSxNQUFWLENBQWlCUixVQUFqQixJQUE4QkgsTUFBdkM7VUFDQSxLQUFLTSxDQUFMLEdBQVNTLEVBQUUsR0FBR08sRUFBTCxHQUFVTixFQUFFLEdBQUdRLEVBQXhCO1VBQ0EsS0FBS2YsQ0FBTCxHQUFTTSxFQUFFLEdBQUdRLEVBQUwsR0FBVVAsRUFBRSxHQUFHUyxFQUF4QjtVQUNBLEtBQUtmLENBQUwsR0FBU08sRUFBRSxHQUFHSyxFQUFMLEdBQVVKLEVBQUUsR0FBR00sRUFBeEI7VUFDQSxLQUFLWixDQUFMLEdBQVNLLEVBQUUsR0FBR00sRUFBTCxHQUFVTCxFQUFFLEdBQUdPLEVBQXhCO1VBQ0E7UUFDQTs7TUFDRCxLQUFLTCx3QkFBY00sZUFBbkI7UUFBb0M7VUFDbkMsSUFBSXZCLFdBQVMsR0FBR0wsUUFBUSxHQUFHLEVBQVgsR0FBZ0JJLE1BQWhDOztVQUNBLEtBQUtJLENBQUwsR0FBU0MsaUJBQVVDLE1BQVYsQ0FBaUJWLFFBQVEsR0FBR0csTUFBNUIsSUFBc0NGLE1BQS9DO1VBQ0EsS0FBS1UsQ0FBTCxHQUFTRixpQkFBVUMsTUFBVixDQUFpQkwsV0FBakIsSUFBOEJILE1BQXZDO1VBQ0EsS0FBS1UsQ0FBTCxHQUFTSCxpQkFBVUksTUFBVixDQUFpQmIsUUFBUSxHQUFHRyxNQUE1QixJQUFzQ0YsTUFBL0M7VUFDQSxLQUFLYSxDQUFMLEdBQVNMLGlCQUFVSSxNQUFWLENBQWlCUixXQUFqQixJQUE4QkgsTUFBdkM7VUFDQTtRQUNBOztNQUNELEtBQUtvQix3QkFBY08sc0JBQW5CO1FBQTJDO1VBQzFDLElBQUlDLENBQUMsR0FBR2IsRUFBRSxHQUFHQSxFQUFMLEdBQVVFLEVBQUUsR0FBR0EsRUFBdkI7VUFDQSxJQUFJWSxHQUFHLEdBQUcsQ0FBVjs7VUFDQSxJQUFJRCxDQUFDLEdBQUcsTUFBUixFQUFnQjtZQUNmQSxDQUFDLEdBQUdFLElBQUksQ0FBQ0MsR0FBTCxDQUFTaEIsRUFBRSxHQUFHRyxFQUFMLEdBQVVGLEVBQUUsR0FBR0MsRUFBeEIsSUFBOEJXLENBQWxDO1lBQ0FiLEVBQUUsSUFBSSxLQUFLcEMsUUFBTCxDQUFjb0IsTUFBcEI7WUFDQWtCLEVBQUUsSUFBSSxLQUFLdEMsUUFBTCxDQUFjcUIsTUFBcEI7WUFDQWdCLEVBQUUsR0FBR0MsRUFBRSxHQUFHVyxDQUFWO1lBQ0FWLEVBQUUsR0FBR0gsRUFBRSxHQUFHYSxDQUFWO1lBQ0FDLEdBQUcsR0FBR0MsSUFBSSxDQUFDRSxLQUFMLENBQVdmLEVBQVgsRUFBZUYsRUFBZixJQUFxQlIsaUJBQVUwQixNQUFyQztVQUNBLENBUEQsTUFPTztZQUNObEIsRUFBRSxHQUFHLENBQUw7WUFDQUUsRUFBRSxHQUFHLENBQUw7WUFDQVksR0FBRyxHQUFHLEtBQUtDLElBQUksQ0FBQ0UsS0FBTCxDQUFXZCxFQUFYLEVBQWVGLEVBQWYsSUFBcUJULGlCQUFVMEIsTUFBMUM7VUFDQTs7VUFDRCxJQUFJQyxFQUFFLEdBQUdwQyxRQUFRLEdBQUdHLE1BQVgsR0FBb0I0QixHQUE3QjtVQUNBLElBQUlNLEVBQUUsR0FBR3JDLFFBQVEsR0FBR0ksTUFBWCxHQUFvQjJCLEdBQXBCLEdBQTBCLEVBQW5DOztVQUNBLElBQUlQLEdBQUUsR0FBR2YsaUJBQVVDLE1BQVYsQ0FBaUIwQixFQUFqQixJQUF1Qm5DLE1BQWhDOztVQUNBLElBQUl3QixHQUFFLEdBQUdoQixpQkFBVUMsTUFBVixDQUFpQjJCLEVBQWpCLElBQXVCbkMsTUFBaEM7O1VBQ0EsSUFBSXdCLEdBQUUsR0FBR2pCLGlCQUFVSSxNQUFWLENBQWlCdUIsRUFBakIsSUFBdUJuQyxNQUFoQzs7VUFDQSxJQUFJMEIsR0FBRSxHQUFHbEIsaUJBQVVJLE1BQVYsQ0FBaUJ3QixFQUFqQixJQUF1Qm5DLE1BQWhDOztVQUNBLEtBQUtNLENBQUwsR0FBU1MsRUFBRSxHQUFHTyxHQUFMLEdBQVVOLEVBQUUsR0FBR1EsR0FBeEI7VUFDQSxLQUFLZixDQUFMLEdBQVNNLEVBQUUsR0FBR1EsR0FBTCxHQUFVUCxFQUFFLEdBQUdTLEdBQXhCO1VBQ0EsS0FBS2YsQ0FBTCxHQUFTTyxFQUFFLEdBQUdLLEdBQUwsR0FBVUosRUFBRSxHQUFHTSxHQUF4QjtVQUNBLEtBQUtaLENBQUwsR0FBU0ssRUFBRSxHQUFHTSxHQUFMLEdBQVVMLEVBQUUsR0FBR08sR0FBeEI7VUFDQTtRQUNBOztNQUNELEtBQUtMLHdCQUFjZ0IsT0FBbkI7TUFDQSxLQUFLaEIsd0JBQWNpQixtQkFBbkI7UUFBd0M7VUFDdkMsSUFBSUMsR0FBRyxHQUFHL0IsaUJBQVVDLE1BQVYsQ0FBaUJWLFFBQWpCLENBQVY7O1VBQ0EsSUFBSXlDLEdBQUcsR0FBR2hDLGlCQUFVSSxNQUFWLENBQWlCYixRQUFqQixDQUFWOztVQUNBLElBQUkwQyxFQUFFLEdBQUcsQ0FBQ3pCLEVBQUUsR0FBR3VCLEdBQUwsR0FBV3RCLEVBQUUsR0FBR3VCLEdBQWpCLElBQXdCLEtBQUs1RCxRQUFMLENBQWNvQixNQUEvQztVQUNBLElBQUkwQyxFQUFFLEdBQUcsQ0FBQ3hCLEVBQUUsR0FBR3FCLEdBQUwsR0FBV3BCLEVBQUUsR0FBR3FCLEdBQWpCLElBQXdCLEtBQUs1RCxRQUFMLENBQWNxQixNQUEvQzs7VUFDQSxJQUFJNEIsRUFBQyxHQUFHRSxJQUFJLENBQUNZLElBQUwsQ0FBVUYsRUFBRSxHQUFHQSxFQUFMLEdBQVVDLEVBQUUsR0FBR0EsRUFBekIsQ0FBUjs7VUFDQSxJQUFJYixFQUFDLEdBQUcsT0FBUixFQUFpQkEsRUFBQyxHQUFHLElBQUlBLEVBQVI7VUFDakJZLEVBQUUsSUFBSVosRUFBTjtVQUNBYSxFQUFFLElBQUliLEVBQU47VUFDQUEsRUFBQyxHQUFHRSxJQUFJLENBQUNZLElBQUwsQ0FBVUYsRUFBRSxHQUFHQSxFQUFMLEdBQVVDLEVBQUUsR0FBR0EsRUFBekIsQ0FBSjtVQUNBLElBQUksS0FBSy9ELElBQUwsQ0FBVXlDLGFBQVYsSUFBMkJDLHdCQUFjZ0IsT0FBekMsSUFDQ3JCLEVBQUUsR0FBR0csRUFBTCxHQUFVRixFQUFFLEdBQUdDLEVBQWYsR0FBb0IsQ0FBckIsS0FBNEIsS0FBS3RDLFFBQUwsQ0FBY29CLE1BQWQsR0FBdUIsQ0FBdkIsSUFBNEIsS0FBS3BCLFFBQUwsQ0FBY3FCLE1BQWQsR0FBdUIsQ0FBL0UsQ0FESixFQUN1RjRCLEVBQUMsR0FBRyxDQUFDQSxFQUFMO1VBQ3ZGLElBQUllLENBQUMsR0FBR2IsSUFBSSxDQUFDYyxFQUFMLEdBQVUsQ0FBVixHQUFjZCxJQUFJLENBQUNFLEtBQUwsQ0FBV1MsRUFBWCxFQUFlRCxFQUFmLENBQXRCOztVQUNBLElBQUlLLEVBQUUsR0FBR2YsSUFBSSxDQUFDUSxHQUFMLENBQVNLLENBQVQsSUFBY2YsRUFBdkI7O1VBQ0EsSUFBSWtCLEVBQUUsR0FBR2hCLElBQUksQ0FBQ1MsR0FBTCxDQUFTSSxDQUFULElBQWNmLEVBQXZCOztVQUNBLElBQUlOLElBQUUsR0FBR2YsaUJBQVVDLE1BQVYsQ0FBaUJQLE1BQWpCLElBQTJCRixNQUFwQzs7VUFDQSxJQUFJd0IsSUFBRSxHQUFHaEIsaUJBQVVDLE1BQVYsQ0FBaUIsS0FBS04sTUFBdEIsSUFBZ0NGLE1BQXpDOztVQUNBLElBQUl3QixJQUFFLEdBQUdqQixpQkFBVUksTUFBVixDQUFpQlYsTUFBakIsSUFBMkJGLE1BQXBDOztVQUNBLElBQUkwQixJQUFFLEdBQUdsQixpQkFBVUksTUFBVixDQUFpQixLQUFLVCxNQUF0QixJQUFnQ0YsTUFBekM7O1VBQ0EsS0FBS00sQ0FBTCxHQUFTa0MsRUFBRSxHQUFHbEIsSUFBTCxHQUFVdUIsRUFBRSxHQUFHckIsSUFBeEI7VUFDQSxLQUFLZixDQUFMLEdBQVMrQixFQUFFLEdBQUdqQixJQUFMLEdBQVVzQixFQUFFLEdBQUdwQixJQUF4QjtVQUNBLEtBQUtmLENBQUwsR0FBUytCLEVBQUUsR0FBR25CLElBQUwsR0FBVXdCLEVBQUUsR0FBR3RCLElBQXhCO1VBQ0EsS0FBS1osQ0FBTCxHQUFTNkIsRUFBRSxHQUFHbEIsSUFBTCxHQUFVdUIsRUFBRSxHQUFHckIsSUFBeEI7VUFDQTtRQUNBO0lBekVGOztJQTJFQSxLQUFLbkIsQ0FBTCxJQUFVLEtBQUszQixRQUFMLENBQWNvQixNQUF4QjtJQUNBLEtBQUtVLENBQUwsSUFBVSxLQUFLOUIsUUFBTCxDQUFjb0IsTUFBeEI7SUFDQSxLQUFLVyxDQUFMLElBQVUsS0FBSy9CLFFBQUwsQ0FBY3FCLE1BQXhCO0lBQ0EsS0FBS1ksQ0FBTCxJQUFVLEtBQUtqQyxRQUFMLENBQWNxQixNQUF4QjtFQUNBO0VBRUQ7OztTQUNBakIsYyxHQUFBLDBCQUFjO0lBQ2IsSUFBSUwsSUFBSSxHQUFHLEtBQUtBLElBQWhCO0lBQ0EsS0FBS2tCLENBQUwsR0FBU2xCLElBQUksQ0FBQ2tCLENBQWQ7SUFDQSxLQUFLQyxDQUFMLEdBQVNuQixJQUFJLENBQUNtQixDQUFkO0lBQ0EsS0FBS0MsUUFBTCxHQUFnQnBCLElBQUksQ0FBQ29CLFFBQXJCO0lBQ0EsS0FBS0MsTUFBTCxHQUFjckIsSUFBSSxDQUFDcUIsTUFBbkI7SUFDQSxLQUFLQyxNQUFMLEdBQWN0QixJQUFJLENBQUNzQixNQUFuQjtJQUNBLEtBQUtDLE1BQUwsR0FBY3ZCLElBQUksQ0FBQ3VCLE1BQW5CO0lBQ0EsS0FBS0MsTUFBTCxHQUFjeEIsSUFBSSxDQUFDd0IsTUFBbkI7RUFDQTtFQUVEOzs7U0FDQTZDLGlCLEdBQUEsNkJBQWlCO0lBQ2hCLE9BQU9qQixJQUFJLENBQUNFLEtBQUwsQ0FBVyxLQUFLdEIsQ0FBaEIsRUFBbUIsS0FBS0osQ0FBeEIsSUFBNkJDLGlCQUFVMEIsTUFBOUM7RUFDQTtFQUVEOzs7U0FDQWUsaUIsR0FBQSw2QkFBaUI7SUFDaEIsT0FBT2xCLElBQUksQ0FBQ0UsS0FBTCxDQUFXLEtBQUtwQixDQUFoQixFQUFtQixLQUFLSCxDQUF4QixJQUE2QkYsaUJBQVUwQixNQUE5QztFQUNBO0VBRUQ7OztTQUNBZ0IsYyxHQUFBLDBCQUFjO0lBQ2IsT0FBT25CLElBQUksQ0FBQ1ksSUFBTCxDQUFVLEtBQUtwQyxDQUFMLEdBQVMsS0FBS0EsQ0FBZCxHQUFrQixLQUFLSSxDQUFMLEdBQVMsS0FBS0EsQ0FBMUMsQ0FBUDtFQUNBO0VBRUQ7OztTQUNBd0MsYyxHQUFBLDBCQUFjO0lBQ2IsT0FBT3BCLElBQUksQ0FBQ1ksSUFBTCxDQUFVLEtBQUtqQyxDQUFMLEdBQVMsS0FBS0EsQ0FBZCxHQUFrQixLQUFLRyxDQUFMLEdBQVMsS0FBS0EsQ0FBMUMsQ0FBUDtFQUNBO0VBRUQ7Ozs7Ozs7Ozs7U0FRQXVDLHNCLEdBQUEsa0NBQXNCO0lBQ3JCLElBQUl2RSxNQUFNLEdBQUcsS0FBS0EsTUFBbEI7O0lBQ0EsSUFBSSxDQUFDQSxNQUFMLEVBQWE7TUFDWixLQUFLUSxFQUFMLEdBQVUsS0FBS3lCLE1BQUwsR0FBYyxLQUFLbEMsUUFBTCxDQUFjaUIsQ0FBdEM7TUFDQSxLQUFLUCxFQUFMLEdBQVUsS0FBS3lCLE1BQUwsR0FBYyxLQUFLbkMsUUFBTCxDQUFja0IsQ0FBdEM7TUFDQSxLQUFLUCxTQUFMLEdBQWlCd0MsSUFBSSxDQUFDRSxLQUFMLENBQVcsS0FBS3RCLENBQWhCLEVBQW1CLEtBQUtKLENBQXhCLElBQTZCQyxpQkFBVTBCLE1BQXhEO01BQ0EsS0FBSzFDLE9BQUwsR0FBZXVDLElBQUksQ0FBQ1ksSUFBTCxDQUFVLEtBQUtwQyxDQUFMLEdBQVMsS0FBS0EsQ0FBZCxHQUFrQixLQUFLSSxDQUFMLEdBQVMsS0FBS0EsQ0FBMUMsQ0FBZjtNQUNBLEtBQUtsQixPQUFMLEdBQWVzQyxJQUFJLENBQUNZLElBQUwsQ0FBVSxLQUFLakMsQ0FBTCxHQUFTLEtBQUtBLENBQWQsR0FBa0IsS0FBS0csQ0FBTCxHQUFTLEtBQUtBLENBQTFDLENBQWY7TUFDQSxLQUFLbkIsT0FBTCxHQUFlLENBQWY7TUFDQSxLQUFLQyxPQUFMLEdBQWVvQyxJQUFJLENBQUNFLEtBQUwsQ0FBVyxLQUFLMUIsQ0FBTCxHQUFTLEtBQUtHLENBQWQsR0FBa0IsS0FBS0MsQ0FBTCxHQUFTLEtBQUtFLENBQTNDLEVBQThDLEtBQUtOLENBQUwsR0FBUyxLQUFLTSxDQUFkLEdBQWtCLEtBQUtILENBQUwsR0FBUyxLQUFLQyxDQUE5RSxJQUFtRkgsaUJBQVUwQixNQUE1RztNQUNBO0lBQ0E7O0lBQ0QsSUFBSWxCLEVBQUUsR0FBR25DLE1BQU0sQ0FBQzBCLENBQWhCO0lBQUEsSUFBbUJVLEVBQUUsR0FBR3BDLE1BQU0sQ0FBQzZCLENBQS9CO0lBQUEsSUFBa0NRLEVBQUUsR0FBR3JDLE1BQU0sQ0FBQzhCLENBQTlDO0lBQUEsSUFBaURRLEVBQUUsR0FBR3RDLE1BQU0sQ0FBQ2dDLENBQTdEO0lBQ0EsSUFBSXdDLEdBQUcsR0FBRyxLQUFLckMsRUFBRSxHQUFHRyxFQUFMLEdBQVVGLEVBQUUsR0FBR0MsRUFBcEIsQ0FBVjtJQUNBLElBQUlvQyxFQUFFLEdBQUcsS0FBS3hDLE1BQUwsR0FBY2pDLE1BQU0sQ0FBQ2lDLE1BQTlCO0lBQUEsSUFBc0N5QyxFQUFFLEdBQUcsS0FBS3hDLE1BQUwsR0FBY2xDLE1BQU0sQ0FBQ2tDLE1BQWhFO0lBQ0EsS0FBSzFCLEVBQUwsR0FBV2lFLEVBQUUsR0FBR25DLEVBQUwsR0FBVWtDLEdBQVYsR0FBZ0JFLEVBQUUsR0FBR3RDLEVBQUwsR0FBVW9DLEdBQXJDO0lBQ0EsS0FBSy9ELEVBQUwsR0FBV2lFLEVBQUUsR0FBR3ZDLEVBQUwsR0FBVXFDLEdBQVYsR0FBZ0JDLEVBQUUsR0FBR3BDLEVBQUwsR0FBVW1DLEdBQXJDO0lBQ0EsSUFBSUcsRUFBRSxHQUFHSCxHQUFHLEdBQUdsQyxFQUFmO0lBQ0EsSUFBSXNDLEVBQUUsR0FBR0osR0FBRyxHQUFHckMsRUFBZjtJQUNBLElBQUkwQyxFQUFFLEdBQUdMLEdBQUcsR0FBR3BDLEVBQWY7SUFDQSxJQUFJMEMsRUFBRSxHQUFHTixHQUFHLEdBQUduQyxFQUFmO0lBQ0EsSUFBSTBDLEVBQUUsR0FBR0osRUFBRSxHQUFHLEtBQUtqRCxDQUFWLEdBQWNtRCxFQUFFLEdBQUcsS0FBSy9DLENBQWpDO0lBQ0EsSUFBSWtELEVBQUUsR0FBR0wsRUFBRSxHQUFHLEtBQUs5QyxDQUFWLEdBQWNnRCxFQUFFLEdBQUcsS0FBSzdDLENBQWpDO0lBQ0EsSUFBSWlELEVBQUUsR0FBR0wsRUFBRSxHQUFHLEtBQUs5QyxDQUFWLEdBQWNnRCxFQUFFLEdBQUcsS0FBS3BELENBQWpDO0lBQ0EsSUFBSXdELEVBQUUsR0FBR04sRUFBRSxHQUFHLEtBQUs1QyxDQUFWLEdBQWM4QyxFQUFFLEdBQUcsS0FBS2pELENBQWpDO0lBQ0EsS0FBS2hCLE9BQUwsR0FBZSxDQUFmO0lBQ0EsS0FBS0YsT0FBTCxHQUFldUMsSUFBSSxDQUFDWSxJQUFMLENBQVVpQixFQUFFLEdBQUdBLEVBQUwsR0FBVUUsRUFBRSxHQUFHQSxFQUF6QixDQUFmOztJQUNBLElBQUksS0FBS3RFLE9BQUwsR0FBZSxNQUFuQixFQUEyQjtNQUMxQixJQUFJd0UsR0FBRyxHQUFHSixFQUFFLEdBQUdHLEVBQUwsR0FBVUYsRUFBRSxHQUFHQyxFQUF6QjtNQUNBLEtBQUtyRSxPQUFMLEdBQWV1RSxHQUFHLEdBQUcsS0FBS3hFLE9BQTFCO01BQ0EsS0FBS0csT0FBTCxHQUFlb0MsSUFBSSxDQUFDRSxLQUFMLENBQVcyQixFQUFFLEdBQUdDLEVBQUwsR0FBVUMsRUFBRSxHQUFHQyxFQUExQixFQUE4QkMsR0FBOUIsSUFBcUN4RCxpQkFBVTBCLE1BQTlEO01BQ0EsS0FBSzNDLFNBQUwsR0FBaUJ3QyxJQUFJLENBQUNFLEtBQUwsQ0FBVzZCLEVBQVgsRUFBZUYsRUFBZixJQUFxQnBELGlCQUFVMEIsTUFBaEQ7SUFDQSxDQUxELE1BS087TUFDTixLQUFLMUMsT0FBTCxHQUFlLENBQWY7TUFDQSxLQUFLQyxPQUFMLEdBQWVzQyxJQUFJLENBQUNZLElBQUwsQ0FBVWtCLEVBQUUsR0FBR0EsRUFBTCxHQUFVRSxFQUFFLEdBQUdBLEVBQXpCLENBQWY7TUFDQSxLQUFLcEUsT0FBTCxHQUFlLENBQWY7TUFDQSxLQUFLSixTQUFMLEdBQWlCLEtBQUt3QyxJQUFJLENBQUNFLEtBQUwsQ0FBVzhCLEVBQVgsRUFBZUYsRUFBZixJQUFxQnJELGlCQUFVMEIsTUFBckQ7SUFDQTtFQUNEO0VBRUQ7OztTQUNBK0IsWSxHQUFBLHNCQUFjQyxLQUFkLEVBQTRCO0lBQzNCLElBQUlDLE1BQU0sR0FBRyxLQUFLLEtBQUs1RCxDQUFMLEdBQVMsS0FBS00sQ0FBZCxHQUFrQixLQUFLSCxDQUFMLEdBQVMsS0FBS0MsQ0FBckMsQ0FBYjtJQUNBLElBQUlkLENBQUMsR0FBR3FFLEtBQUssQ0FBQ3JFLENBQU4sR0FBVSxLQUFLaUIsTUFBdkI7SUFBQSxJQUErQmhCLENBQUMsR0FBR29FLEtBQUssQ0FBQ3BFLENBQU4sR0FBVSxLQUFLaUIsTUFBbEQ7SUFDQW1ELEtBQUssQ0FBQ3JFLENBQU4sR0FBVUEsQ0FBQyxHQUFHLEtBQUtnQixDQUFULEdBQWFzRCxNQUFiLEdBQXNCckUsQ0FBQyxHQUFHLEtBQUtZLENBQVQsR0FBYXlELE1BQTdDO0lBQ0FELEtBQUssQ0FBQ3BFLENBQU4sR0FBVUEsQ0FBQyxHQUFHLEtBQUtTLENBQVQsR0FBYTRELE1BQWIsR0FBc0J0RSxDQUFDLEdBQUcsS0FBS2MsQ0FBVCxHQUFhd0QsTUFBN0M7SUFDQSxPQUFPRCxLQUFQO0VBQ0E7RUFFRDs7O1NBQ0FFLFksR0FBQSxzQkFBY0MsS0FBZCxFQUE0QjtJQUMzQixJQUFJeEUsQ0FBQyxHQUFHd0UsS0FBSyxDQUFDeEUsQ0FBZDtJQUFBLElBQWlCQyxDQUFDLEdBQUd1RSxLQUFLLENBQUN2RSxDQUEzQjtJQUNBdUUsS0FBSyxDQUFDeEUsQ0FBTixHQUFVQSxDQUFDLEdBQUcsS0FBS1UsQ0FBVCxHQUFhVCxDQUFDLEdBQUcsS0FBS1ksQ0FBdEIsR0FBMEIsS0FBS0ksTUFBekM7SUFDQXVELEtBQUssQ0FBQ3ZFLENBQU4sR0FBVUQsQ0FBQyxHQUFHLEtBQUtjLENBQVQsR0FBYWIsQ0FBQyxHQUFHLEtBQUtlLENBQXRCLEdBQTBCLEtBQUtFLE1BQXpDO0lBQ0EsT0FBT3NELEtBQVA7RUFDQTtFQUVEOzs7U0FDQUMsb0IsR0FBQSw4QkFBc0JDLGFBQXRCLEVBQTJDO0lBQzFDLElBQUkvQixHQUFHLEdBQUdoQyxpQkFBVUksTUFBVixDQUFpQjJELGFBQWpCLENBQVY7SUFBQSxJQUEyQ2hDLEdBQUcsR0FBRy9CLGlCQUFVQyxNQUFWLENBQWlCOEQsYUFBakIsQ0FBakQ7O0lBQ0EsT0FBT3hDLElBQUksQ0FBQ0UsS0FBTCxDQUFXLEtBQUsxQixDQUFMLEdBQVNpQyxHQUFULEdBQWUsS0FBSzdCLENBQUwsR0FBUzRCLEdBQW5DLEVBQXdDLEtBQUsxQixDQUFMLEdBQVMwQixHQUFULEdBQWUsS0FBSzdCLENBQUwsR0FBUzhCLEdBQWhFLElBQXVFaEMsaUJBQVUwQixNQUFqRixHQUEwRixLQUFLbkMsUUFBL0YsR0FBMEcsS0FBS0csTUFBdEg7RUFDQTtFQUVEOzs7U0FDQXNFLG9CLEdBQUEsOEJBQXNCQyxhQUF0QixFQUEyQztJQUMxQ0EsYUFBYSxJQUFJLEtBQUsxRSxRQUFMLEdBQWdCLEtBQUtHLE1BQXRDOztJQUNBLElBQUlzQyxHQUFHLEdBQUdoQyxpQkFBVUksTUFBVixDQUFpQjZELGFBQWpCLENBQVY7SUFBQSxJQUEyQ2xDLEdBQUcsR0FBRy9CLGlCQUFVQyxNQUFWLENBQWlCZ0UsYUFBakIsQ0FBakQ7O0lBQ0EsT0FBTzFDLElBQUksQ0FBQ0UsS0FBTCxDQUFXTSxHQUFHLEdBQUcsS0FBSzVCLENBQVgsR0FBZTZCLEdBQUcsR0FBRyxLQUFLM0IsQ0FBckMsRUFBd0MwQixHQUFHLEdBQUcsS0FBS2hDLENBQVgsR0FBZWlDLEdBQUcsR0FBRyxLQUFLOUIsQ0FBbEUsSUFBdUVGLGlCQUFVMEIsTUFBeEY7RUFDQTtFQUVEOzs7Ozs7U0FJQXdDLFcsR0FBQSxxQkFBYUMsT0FBYixFQUE0QjtJQUMzQixJQUFJcEUsQ0FBQyxHQUFHLEtBQUtBLENBQWI7SUFBQSxJQUFnQkcsQ0FBQyxHQUFHLEtBQUtBLENBQXpCO0lBQUEsSUFBNEJDLENBQUMsR0FBRyxLQUFLQSxDQUFyQztJQUFBLElBQXdDRSxDQUFDLEdBQUcsS0FBS0EsQ0FBakQ7O0lBQ0EsSUFBSTBCLEdBQUcsR0FBRy9CLGlCQUFVQyxNQUFWLENBQWlCa0UsT0FBakIsQ0FBVjtJQUFBLElBQXFDbkMsR0FBRyxHQUFHaEMsaUJBQVVJLE1BQVYsQ0FBaUIrRCxPQUFqQixDQUEzQzs7SUFDQSxLQUFLcEUsQ0FBTCxHQUFTZ0MsR0FBRyxHQUFHaEMsQ0FBTixHQUFVaUMsR0FBRyxHQUFHN0IsQ0FBekI7SUFDQSxLQUFLRCxDQUFMLEdBQVM2QixHQUFHLEdBQUc3QixDQUFOLEdBQVU4QixHQUFHLEdBQUczQixDQUF6QjtJQUNBLEtBQUtGLENBQUwsR0FBUzZCLEdBQUcsR0FBR2pDLENBQU4sR0FBVWdDLEdBQUcsR0FBRzVCLENBQXpCO0lBQ0EsS0FBS0UsQ0FBTCxHQUFTMkIsR0FBRyxHQUFHOUIsQ0FBTixHQUFVNkIsR0FBRyxHQUFHMUIsQ0FBekI7RUFDQSxDIiwibmFtZXMiOlsiQm9uZSIsImRhdGEiLCJza2VsZXRvbiIsInBhcmVudCIsIkFycmF5IiwiRXJyb3IiLCJzZXRUb1NldHVwUG9zZSIsImlzQWN0aXZlIiwiYWN0aXZlIiwidXBkYXRlIiwidXBkYXRlV29ybGRUcmFuc2Zvcm1XaXRoIiwiYXgiLCJheSIsImFyb3RhdGlvbiIsImFzY2FsZVgiLCJhc2NhbGVZIiwiYXNoZWFyWCIsImFzaGVhclkiLCJ1cGRhdGVXb3JsZFRyYW5zZm9ybSIsIngiLCJ5Iiwicm90YXRpb24iLCJzY2FsZVgiLCJzY2FsZVkiLCJzaGVhclgiLCJzaGVhclkiLCJyb3RhdGlvblkiLCJzeCIsInN5IiwiYSIsIk1hdGhVdGlscyIsImNvc0RlZyIsImIiLCJjIiwic2luRGVnIiwiZCIsIndvcmxkWCIsIndvcmxkWSIsInBhIiwicGIiLCJwYyIsInBkIiwidHJhbnNmb3JtTW9kZSIsIlRyYW5zZm9ybU1vZGUiLCJOb3JtYWwiLCJsYSIsImxiIiwibGMiLCJsZCIsIk9ubHlUcmFuc2xhdGlvbiIsIk5vUm90YXRpb25PclJlZmxlY3Rpb24iLCJzIiwicHJ4IiwiTWF0aCIsImFicyIsImF0YW4yIiwicmFkRGVnIiwicngiLCJyeSIsIk5vU2NhbGUiLCJOb1NjYWxlT3JSZWZsZWN0aW9uIiwiY29zIiwic2luIiwiemEiLCJ6YyIsInNxcnQiLCJyIiwiUEkiLCJ6YiIsInpkIiwiZ2V0V29ybGRSb3RhdGlvblgiLCJnZXRXb3JsZFJvdGF0aW9uWSIsImdldFdvcmxkU2NhbGVYIiwiZ2V0V29ybGRTY2FsZVkiLCJ1cGRhdGVBcHBsaWVkVHJhbnNmb3JtIiwicGlkIiwiZHgiLCJkeSIsImlhIiwiaWQiLCJpYiIsImljIiwicmEiLCJyYiIsInJjIiwicmQiLCJkZXQiLCJ3b3JsZFRvTG9jYWwiLCJ3b3JsZCIsImludkRldCIsImxvY2FsVG9Xb3JsZCIsImxvY2FsIiwid29ybGRUb0xvY2FsUm90YXRpb24iLCJ3b3JsZFJvdGF0aW9uIiwibG9jYWxUb1dvcmxkUm90YXRpb24iLCJsb2NhbFJvdGF0aW9uIiwicm90YXRlV29ybGQiLCJkZWdyZWVzIl0sInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uL3NyYy9Cb25lLnRzIl0sInNvdXJjZXNDb250ZW50IjpbbnVsbF19